import { useMemo } from 'react';
import { usePermission } from '../../../../hooks/usePermission';
import type { CompareData, CompareFeature } from './types';

interface Props {
  compareData: CompareData | null;
}

/** 按 groupName 分组，保持后端返回顺序 */
function groupFeatures(features: CompareFeature[]): Array<[string, CompareFeature[]]> {
  const map = new Map<string, CompareFeature[]>();
  for (const f of features) {
    const g = f.groupName || '其他';
    const arr = map.get(g);
    if (arr) arr.push(f);
    else map.set(g, [f]);
  }
  return [...map.entries()];
}

/**
 * 功能权限对比表（等级列由后端 compare 接口动态下发，支持任意数量等级）。
 * 单元格根据 feature.access 中对应 levelCode 的 allowed 渲染对勾/叉；当前用户所在等级列高亮。
 */
export function FeatureCompare({ compareData }: Props) {
  const { levelCode } = usePermission();
  const levels = useMemo(() => compareData?.levels ?? [], [compareData]);
  const features = useMemo(() => compareData?.features ?? [], [compareData]);
  const groups = useMemo(() => groupFeatures(features), [features]);

  const allowedOf = (f: CompareFeature, code: string) => f.access.find((a) => a.levelCode === code)?.allowed ?? false;

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-3">功能权限对比</h3>
      {levels.length > 0 && features.length > 0 ? (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {/* 表头：功能 + 各等级 */}
          <div
            className="grid text-xs font-semibold text-center bg-gray-50 border-b border-gray-200"
            style={{ gridTemplateColumns: `minmax(150px,2fr) repeat(${levels.length}, minmax(76px,1fr))` }}
          >
            <div className="px-4 py-3 text-left text-gray-500">功能</div>
            {levels.map((l) => (
              <div
                key={l.code}
                className={`py-3 border-l border-gray-200 ${
                  l.code === levelCode ? 'text-amber-600 bg-amber-50' : 'text-gray-600'
                }`}
              >
                {l.name}
                {l.code === levelCode && <span className="ml-1 text-[10px] text-amber-500">当前</span>}
              </div>
            ))}
          </div>

          {/* 分组 + 功能行 */}
          {groups.map(([groupName, rows]) => (
            <div key={groupName}>
              <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">
                {groupName}
              </div>
              {rows.map((f, idx) => (
                <div
                  key={f.key}
                  className={`grid text-xs text-center border-b border-gray-100 last:border-0 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                  }`}
                  style={{ gridTemplateColumns: `minmax(150px,2fr) repeat(${levels.length}, minmax(76px,1fr))` }}
                >
                  <div className="px-4 py-2.5 text-left text-gray-700 flex items-center gap-1.5">
                    <span>{f.name}</span>
                    {f.description && (
                      <span className="text-gray-400 hidden sm:inline" title={f.description}>
                        <i className="ri-information-line" />
                      </span>
                    )}
                  </div>
                  {levels.map((l) => {
                    const ok = allowedOf(f, l.code);
                    const isCurrent = l.code === levelCode;
                    return (
                      <div
                        key={l.code}
                        className={`py-2.5 flex items-center justify-center border-l border-gray-100 ${
                          isCurrent ? 'bg-amber-50/40' : ''
                        }`}
                      >
                        <i
                          className={`text-base ${
                            ok
                              ? isCurrent
                                ? 'ri-checkbox-circle-fill text-amber-500'
                                : 'ri-checkbox-circle-fill text-teal-500'
                              : 'ri-close-circle-fill text-gray-300'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-gray-400 text-center py-6 bg-gray-50 rounded-xl border border-gray-200">
          暂无功能权限数据，请稍后重试或联系管理员配置
        </div>
      )}
    </div>
  );
}
